/**
 * Reseller onboarding Done step — terminal confirmation + dashboard hand-off
 * (CLA-587).
 *
 * The wizard's terminal stop, reached once the agreement is signed and Stripe
 * Connect is complete. It confirms both and hands the reseller off to their
 * existing reseller dashboard at {@link RESELLER_DASHBOARD_PATH}.
 *
 * Purely presentational — no atoms needed (the rail's guard + resume resolver
 * only route a reseller here once both prior stops are complete).
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { RESELLER_DASHBOARD_PATH } from "~/lib/reseller-onboarding-steps.ts";

export const Route = createFileRoute("/clawinstall/reseller_/onboarding/done")({
  component: OnboardingResellerDoneStep,
});

/** One confirmed line item in the Done summary. */
export function DoneSummaryItem({ label }: { readonly label: string }) {
  return (
    <li className="flex items-center gap-2 text-sm text-on-surface">
      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      {label}
    </li>
  );
}

export function OnboardingResellerDoneStep() {
  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <p className="flex items-center gap-2 text-sm font-medium text-emerald-500">
          <CheckCircle2 className="h-5 w-5" /> You&apos;re all set
        </p>
        <h1 className="text-2xl font-bold text-on-surface">Your reseller account is ready</h1>
        <p className="text-sm text-on-surface-variant">
          Onboarding is complete. Your dashboard is where you&apos;ll manage your customers and their agents from here
          on.
        </p>
      </header>
      <ul className="flex flex-col gap-2" data-testid="done-summary">
        <DoneSummaryItem label="Reseller agreement signed" />
        <DoneSummaryItem label="Stripe Connect payouts enabled" />
      </ul>
      <div>
        <a
          href={RESELLER_DASHBOARD_PATH}
          className="inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-white"
          data-testid="done-dashboard"
        >
          Go to your dashboard
        </a>
      </div>
    </section>
  );
}
